/**
 * Claiming an invitation.
 *
 * The invitee arrives with no session, sets a password, and is signed in straight away —
 * so there is no second step for anyone to forget.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { Field, Notice } from "@/components/ui";

const MIN_LENGTH = 12;

export function AcceptInvitation({ onAccepted }: { onAccepted: () => void }) {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const mismatch = confirmation.length > 0 && confirmation !== password;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.acceptInvitation({
        token,
        password,
        display_name: displayName || null,
      });
      onAccepted();
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="signin">
        <div className="signin__card">
          <h1>Invitation link</h1>
          <Notice tone="danger">
            This link is missing its token. Ask an administrator for a fresh one.
          </Notice>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="signin__mark" aria-hidden="true">
          D
        </div>
        <h1>Set your password</h1>
        <p className="signin__lede">
          Choose a password and you are in. Nobody else sees it, including whoever
          invited you.
        </p>

        {problem && (
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <Notice tone="danger">{problem}</Notice>
          </div>
        )}

        <div className="stack" style={{ gap: 12, textAlign: "left" }}>
          <Field
            label="Your name"
            hint="How you will appear to colleagues. Optional."
            htmlFor="invite-name"
          >
            <input
              id="invite-name"
              className="input"
              value={displayName}
              autoComplete="name"
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </Field>

          <Field
            label="Password"
            hint={`At least ${MIN_LENGTH} characters. A phrase you can remember beats a string you cannot.`}
            error={tooShort ? `Use at least ${MIN_LENGTH} characters.` : undefined}
            required
            htmlFor="invite-password"
          >
            <input
              id="invite-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field
            label="Confirm password"
            error={mismatch ? "These do not match." : undefined}
            required
            htmlFor="invite-confirm"
          >
            <input
              id="invite-confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              required
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          <button
            type="submit"
            className="button button--primary"
            style={{ width: "100%", alignSelf: "stretch" }}
            disabled={busy || mismatch || tooShort || !password}
          >
            {busy ? "Setting your password…" : "Set password and sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}
